// src/components/dashboard/InvitationCard.js

import { getIncidentSeverity } from "../../constants/severity";
import { generateReportId } from "../../services/incidentReportService";
import { formatDuration } from "../../utils/duration";
import { incidentAgeMs } from "../../utils/queueMetrics";

/**
 * A customer has asked for this expert by name.
 *
 * Extracted from the ~50 lines of inline markup in DashboardHelper, which had
 * drifted away from every other incident surface: it showed no severity, no
 * incident reference, and no age, so an invitation to a P1 outage from an hour
 * ago looked identical to a P4 question from a minute ago.
 */
export default function InvitationCard({
  invitation,
  onAccept,
  onDecline,
  busy = false,
}) {
  const problem = invitation.problem || {};
  const severity = getIncidentSeverity(problem);

  return (
    <div
      className={`flex flex-col rounded-xl border border-slate-200 border-l-2 bg-white p-4 ${severity.accentClass}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${severity.chipClass}`}
          title={severity.description}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${severity.dotClass}`}
            aria-hidden="true"
          />
          {severity.label}
        </span>
        {problem.id && (
          <span className="font-mono text-xs text-slate-400">
            {generateReportId(problem.id)}
          </span>
        )}
        <span className="ml-auto text-xs text-slate-500">
          Reported {formatDuration(incidentAgeMs(problem))} ago
        </span>
      </div>

      <h3 className="mt-2 text-sm font-semibold text-slate-900">
        {problem.title || "Untitled incident"}
      </h3>

      {problem.description && (
        <p className="mt-1 line-clamp-3 text-sm text-slate-600">
          {problem.description}
        </p>
      )}

      {problem.tags?.length > 0 && (
        <p className="mt-2 truncate text-xs text-slate-500">
          {problem.tags.slice(0, 5).map((tag) => `#${tag}`).join(" ")}
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => onAccept(invitation.id, invitation.problemId)}
          disabled={busy}
          className="flex-1 rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? "Accepting…" : "Accept & open workspace"}
        </button>
        <button
          type="button"
          onClick={() => onDecline(invitation.id)}
          disabled={busy}
          className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
        >
          Decline
        </button>
      </div>
    </div>
  );
}
